/**
 * WHAT HAPPENED TO THE WRITE — one classification, used by every screen that performs one.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * SIX ANSWERS, AND THE WHOLE POINT IS THAT THEY DO NOT COLLAPSE INTO "אירעה תקלה".
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The Owner's direction is that materially different states must not become a generic 403/409/500
 * when the server already knows the actionable reason. That direction is only kept if the CLIENT
 * keeps the answers apart too — and it will not stay kept if each of a dozen screens writes its own
 * `catch` block. The first one that writes `catch { setError(true) }` erases the whole contract for
 * its own screen, quietly, and nothing fails.
 *
 * So this module is the one place an error from `client.ts` becomes a product state:
 *
 *   denied       the GATE refused. A `DenyBody`, rendered by `<DenialNotice>`, uniform outside the
 *                tenant and named inside it. This is the only member that is an access decision.
 *   stepUp       the handler refused because the session is stale or a factor is missing. NOT a
 *                denial; it carries `need`, which names the door to open.
 *   conflict     the gate ALLOWED it and the write still could not happen. Carries the actionable
 *                half — the excess, the missing fields, the permitted states, the count and limit.
 *   notFound     the object is not there, or is not visible to this caller, and the product does not
 *                distinguish the two on purpose.
 *   unreachable  the request never got an answer. Distinct from a failure BECAUSE A WRITE IS NEVER
 *                RETRIED: the client cannot know whether the server committed, so the honest sentence
 *                is "we could not reach the server", never "it did not happen".
 *   error        something else. The only thing shown is a support reference (09 §9.1 V3).
 *
 * ── WHAT THIS MODULE MAY NOT DO ─────────────────────────────────────────────────────────────────
 *
 * It may not decide permission, and it cannot: it never sees a capability, a role or a session. It
 * reads which ERROR CLASS the client produced and copies the server's own body across. Every screen
 * then hands the result to a mapper that turns it into Hebrew — no screen renders a member of a body.
 */

import {
  ApiError,
  ConflictError,
  DeniedError,
  NotFoundError,
  RefusedError,
  StepUpRequiredError,
} from './client';
import type { ConflictBody } from './client';
import type { DenyBody, StepUpNeed } from './contract';
import { stepUpNeedOf } from './stepUpText';

export type ActionFailure =
  | { readonly kind: 'denied'; readonly body: DenyBody }
  | { readonly kind: 'stepUp'; readonly need: StepUpNeed | null }
  /*
   * THE REFERENCE TRAVELS WITH THE REFUSAL. A conflict is the one failure class an operator is
   * expected to act on, and the Owner's instruction is that the message carry a fault reference:
   * without it, "the server refused" cannot be looked up in the audit trail afterwards.
   * `ConflictError` has carried it since it was written; this member dropped it.
   */
  | { readonly kind: 'conflict'; readonly body: ConflictBody; readonly requestUid: string }
  /**
   * A NAMED REFUSAL WITH THE SERVER'S OWN REMEDY. 422.
   *
   * `error` is the route's refusal word, which a screen may BRANCH on — `provider_page_unavailable`
   * is the reason `SignupVerifyScreen` falls back to the direct check instead of showing a
   * failure. `remedy` is the sentence the server wrote for the person; `detail` is the extra the
   * refusal carried, which for a clearing refusal is the provider's own code and description.
   *
   * THE REFERENCE TRAVELS TOO, for the same reason `conflict` carries one: a refusal somebody
   * reports has to be findable in the journal.
   */
  | {
      readonly kind: 'refused';
      readonly reason: string;
      readonly remedy: string;
      readonly detail: string | null;
      readonly requestUid: string;
    }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'unreachable' }
  | { readonly kind: 'error'; readonly requestUid: string };

/**
 * Classifies a thrown value. Total by construction: anything unrecognised becomes `unreachable`,
 * which is the SAFE default for a write — it claims nothing about whether the server committed.
 */
export function classifyActionError(err: unknown): ActionFailure {
  if (err instanceof StepUpRequiredError) {
    // `need` is validated into the union here rather than trusted: a value this build has no Hebrew
    // for becomes `null`, and the screen then says less instead of naming the wrong screen.
    return { kind: 'stepUp', need: stepUpNeedOf({ need: err.need }) };
  }
  if (err instanceof DeniedError) return { kind: 'denied', body: err.body };
  if (err instanceof ConflictError) {
    return { kind: 'conflict', body: err.body, requestUid: err.requestUid };
  }
  if (err instanceof RefusedError) {
    /*
     * READ DEFENSIVELY. The body is JSON from the network: a refusal whose shape this build does
     * not recognise becomes a refusal with an empty remedy rather than `undefined` reaching a
     * screen, and the notice then says less instead of rendering nothing.
     */
    const body = (typeof err.body === 'object' && err.body !== null ? err.body : {}) as Record<
      string,
      unknown
    >;
    return {
      kind: 'refused',
      reason: typeof body['error'] === 'string' ? body['error'] : 'refused',
      remedy: typeof body['remedy'] === 'string' ? body['remedy'] : '',
      detail: typeof body['detail'] === 'string' ? body['detail'] : null,
      requestUid: err.requestUid,
    };
  }
  if (err instanceof NotFoundError) return { kind: 'notFound' };
  // `status === 0` is the transport failure `NetworkError` carries.
  if (err instanceof ApiError && err.status === 0) return { kind: 'unreachable' };
  if (err instanceof ApiError) return { kind: 'error', requestUid: err.requestUid };
  return { kind: 'unreachable' };
}

export interface ActionFailureText {
  readonly titleHe: string;
  readonly bodyHe: string;
  readonly nextStepHe: string;
}

/**
 * The two failures that have no server-supplied detail, in words.
 *
 * `unreachable` is the one worth reading twice. It does NOT say "the action did not happen", because
 * nobody knows that: the connection died and the write was not retried, precisely because a repeated
 * write is the worse outcome. So it says what is true — no answer arrived — and tells the user to
 * look before repeating.
 */
export const UNREACHABLE_HE: ActionFailureText = {
  titleHe: 'לא התקבלה תשובה מהשרת',
  bodyHe:
    'הבקשה נשלחה ולא חזרה ממנה תשובה, ולכן לא ניתן לדעת אם היא בוצעה. המערכת אינה שולחת פעולה כזו ' +
    'שוב מעצמה — שליחה כפולה של פעולה שכבר בוצעה היא התוצאה הגרועה יותר.',
  nextStepHe: 'יש לרענן את המסך ולבדוק את המצב העדכני לפני ניסיון נוסף.',
};

/**
 * A **READ** THAT ANSWERS 404, WHICH IS A DIFFERENT FACT FROM THE ONE BELOW. **2026-11-03.**
 *
 * `NOT_FOUND_HE` is written for an ACT on a named object -- *"we could not find the item the
 * action refers to, so nothing was changed to it"*. On a screen that is merely READING a
 * collection both halves are wrong: nothing was being changed, and what was not found is an
 * ADDRESS rather than an item.
 *
 * ── AND ON AN ESTATE IT USUALLY MEANS ONE THING ────────────────────────────────────────────────
 *
 * The Owner opened the collections screen on 2026-11-03 and was shown *"הפריט אינו זמין"* where
 * the arrears table belongs. The screen was right and the sentence was wrong: the installation had
 * stopped at stage F, so `current` had already been switched to the new release -- which is what
 * nginx serves the UI from -- while the service was still the process started from the previous
 * one. A NEW screen was asking an OLD API for a route that release does not declare, and a
 * declared read answering 404 means exactly that.
 *
 * So the sentence names it. It does not CLAIM it -- a 404 could also be a genuinely absent
 * object -- which is why it says what to check rather than what happened.
 */
export const READ_NOT_SERVED_HE: ActionFailureText = {
  titleHe: 'המסך הזה לא נענה על ידי השרת',
  bodyHe:
    'הכתובת שהמסך הזה פונה אליה אינה מוכרת לשירות שרץ כרגע. בדרך כלל זה אומר שהממשק חדש יותר ' +
    'מהשירות — למשל כשהתקנה נעצרה באמצע והשירות לא הופעל מחדש עם הגרסה החדשה.',
  nextStepHe:
    'יש לבדוק במסך „בריאות המערכת" איזו גרסה רצה, ולהשלים את ההתקנה. רענון לבדו לא ישנה את התוצאה.',
};

export const NOT_FOUND_HE: ActionFailureText = {
  titleHe: 'הפריט אינו זמין',
  // Careful not to confirm or deny that the object exists anywhere: that is the gate's disclosure
  // rule, and a screen that leaked it would undo it.
  bodyHe: 'לא הצלחנו למצוא את הפריט שאליו מפנה הפעולה, ולכן לא נעשה בו שינוי.',
  nextStepHe: 'רענון המסך ובחירה מתוך הרשימה המעודכנת.',
};
